import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Box,
  Stack,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GL_SYNC_DATES, TB_SYNC_DATES } from "../../config/syncDefaults";

const modalTheme = createTheme({
  zIndex: {
    modal: 20000,
  },
});

export const QuickBooksSyncModal = ({
  isOpen,
  onClose,
  customer,
  customerIds = [],
  selectedCount = 0,
  onSyncConfirmed,
  onShowFullNote,
  isAdmin = false,
  isLoading = false,
}) => {
  const [activeSection, setActiveSection] = useState("changes");
  const [customTransactions, setCustomTransactions] = useState(false);
  const [customBalances, setCustomBalances] = useState(false);
  const [glDates, setGlDates] = useState(GL_SYNC_DATES);
  const [tbDates, setTbDates] = useState(TB_SYNC_DATES);
  const [fullSyncImport, setFullSyncImport] = useState(true);
  const [fullSyncFetch, setFullSyncFetch] = useState(false);

  const openingBalanceDate = useMemo(
    () => customer?.opening_balance_date || null,
    [customer?.opening_balance_date]
  );

  const addDays = useCallback((dateString, days) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    const dateValue = new Date(year, month - 1, day + days);
    const yyyy = dateValue.getFullYear();
    const mm = String(dateValue.getMonth() + 1).padStart(2, "0");
    const dd = String(dateValue.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const operationalStartDate = useMemo(
    () => addDays(openingBalanceDate, 1),
    [addDays, openingBalanceDate]
  );

  useEffect(() => {
    if (isOpen) {
      setActiveSection("changes");
      setCustomTransactions(false);
      setCustomBalances(false);
      setFullSyncFetch(false);
      setFullSyncImport(true);
      setGlDates((prev) => {
        if (operationalStartDate) {
          return {
            startDate: operationalStartDate,
            endDate: GL_SYNC_DATES.endDate,
          };
        }
        return GL_SYNC_DATES;
      });
      setTbDates(
        openingBalanceDate
          ? { startDate: openingBalanceDate, endDate: openingBalanceDate }
          : TB_SYNC_DATES
      );
    }
  }, [isOpen, operationalStartDate, openingBalanceDate]);

  const totalSelectedCount = useMemo(() => {
    if (selectedCount && selectedCount > 0) {
      return selectedCount;
    }
    if (Array.isArray(customerIds) && customerIds.length > 0) {
      return customerIds.length;
    }
    return customer?.id ? 1 : 0;
  }, [customer?.id, customerIds, selectedCount]);

  const selectionLabel = useMemo(() => {
    if (totalSelectedCount > 1) {
      return `${totalSelectedCount} accounts`;
    }
    return customer?.name || "this customer";
  }, [customer?.name, totalSelectedCount]);

  const noteText = useMemo(() => {
    if (totalSelectedCount > 1) {
      return "";
    }

    if (customer && customer.note) {
      if (typeof customer.note === "string") {
        return customer.note;
      }
      if (typeof customer.note === "object" && customer.note.note) {
        return customer.note.note;
      }
    }
    return "";
  }, [customer, totalSelectedCount]);

  const notePreview = useMemo(() => {
    if (!noteText) {
      return { preview: "", isTruncated: false };
    }
    const lines = noteText.split(/\r?\n/);
    if (lines.length <= 4) {
      return { preview: noteText, isTruncated: false };
    }
    return { preview: `${lines.slice(0, 4).join("\n")}…`, isTruncated: true };
  }, [noteText]);

  const resolvedCustomerIds = useMemo(() => {
    if (Array.isArray(customerIds) && customerIds.length > 0) {
      return customerIds.filter(Boolean);
    }
    if (customer?.id) {
      return [customer.id];
    }
    return [];
  }, [customer?.id, customerIds]);

  const handleSubmit = () => {
    if (typeof onSyncConfirmed !== "function") {
      return;
    }

    const targetCustomerIds = resolvedCustomerIds;
    if (!Array.isArray(targetCustomerIds) || targetCustomerIds.length === 0) {
      return;
    }

    const primaryCustomerId = targetCustomerIds[0];
    const isBulkSelection = targetCustomerIds.length > 1;

    const buildPayload = (payload) => ({
      ...payload,
      customerIds: targetCustomerIds,
      ...(isBulkSelection ? {} : { customerId: primaryCustomerId }),
    });

    if (activeSection === "changes") {
      onSyncConfirmed(
        buildPayload({
          syncType: "transactions",
          fetchMode: "cdc",
          importMode: "import",
        })
      );
      return;
    }

    if (activeSection === "full") {
      let importModeValue = "import";
      if (fullSyncFetch && !fullSyncImport) {
        importModeValue = "raw_only";
      } else if (!fullSyncFetch && !fullSyncImport) {
        importModeValue = "none";
      }
      onSyncConfirmed(
        buildPayload({
          syncType: "all",
          fetchMode: "full",
          importMode: importModeValue,
        })
      );
      return;
    }

    let syncTypeValue = "all";
    if (customTransactions && !customBalances) {
      syncTypeValue = "transactions";
    } else if (customBalances && !customTransactions) {
      syncTypeValue = "balances";
    }

    onSyncConfirmed(
      buildPayload({
        syncType: syncTypeValue,
        fetchMode: "range",
        importMode: "import",
        gl_start_date: customTransactions ? glDates.startDate : undefined,
        gl_end_date: customTransactions ? glDates.endDate : undefined,
        tb_start_date: customBalances ? tbDates.startDate : undefined,
        tb_end_date: customBalances ? tbDates.endDate : undefined,
      })
    );
  };

  const disableCustomSubmit =
    (customTransactions && (!glDates.startDate || !glDates.endDate)) ||
    (customBalances && (!tbDates.startDate || !tbDates.endDate));

  return (
    <ThemeProvider theme={modalTheme}>
    <Dialog
      open={Boolean(isOpen)}
      onClose={isLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Sync QuickBooks Data</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Configure sync settings for <strong>{selectionLabel}</strong>
        </Typography>

        {isAdmin && notePreview.preview && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              borderLeft: "4px solid #FB923C",
              backgroundColor: "rgba(251, 146, 60, 0.12)",
            }}
          >
            <Typography variant="subtitle2" color="#C2410C">
              Note
            </Typography>
            <Typography
              variant="body2"
              color="#7C2D12"
              sx={{ whiteSpace: "pre-line" }}
            >
              {notePreview.preview}
            </Typography>
            {notePreview.isTruncated && typeof onShowFullNote === "function" && (
              <Button
                size="small"
                variant="text"
                sx={{ mt: 0.5, color: "#FB923C" }}
                onClick={() => onShowFullNote(noteText)}
              >
                Show more
              </Button>
            )}
          </Box>
        )}

        <Accordion
          expanded={activeSection === "changes"}
          onChange={() =>
            setActiveSection((prev) => (prev === "changes" ? "" : "changes"))
          }
          sx={{ mt: 3 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Updates-Only Sync</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">
              Sync only changes since the last successful sync.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={activeSection === "full"}
          onChange={() =>
            setActiveSection((prev) => (prev === "full" ? "" : "full"))
          }
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Full Sync</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Sync full QuickBooks history for this customer.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fullSyncFetch}
                    onChange={(e) => setFullSyncFetch(e.target.checked)}
                  />
                }
                label="Fetch (download raw data only)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fullSyncImport}
                    onChange={(e) => setFullSyncImport(e.target.checked)}
                  />
                }
                label="Import (process into journal entries)"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={activeSection === "custom"}
          onChange={() =>
            setActiveSection((prev) => (prev === "custom" ? "" : "custom"))
          }
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Range Sync</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customTransactions}
                    onChange={(e) => setCustomTransactions(e.target.checked)}
                  />
                }
                label="Transactions"
              />
              <Stack direction="row" spacing={2} sx={{ pl: 3 }}>
                <TextField
                  label="GL Start Date"
                  type="date"
                  size="small"
                  value={glDates.startDate}
                  onChange={(e) =>
                    setGlDates((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                  disabled={!customTransactions}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="GL End Date"
                  type="date"
                  size="small"
                  value={glDates.endDate}
                  onChange={(e) =>
                    setGlDates((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  disabled={!customTransactions}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={customBalances}
                    onChange={(e) => setCustomBalances(e.target.checked)}
                  />
                }
                label="Balances"
              />
              <Stack direction="row" spacing={2} sx={{ pl: 3 }}>
                <TextField
                  label="TB Start Date"
                  type="date"
                  size="small"
                  value={tbDates.startDate}
                  onChange={(e) =>
                    setTbDates((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                  disabled={!customBalances}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="TB End Date"
                  type="date"
                  size="small"
                  value={tbDates.endDate}
                  onChange={(e) =>
                    setTbDates((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  disabled={!customBalances}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || (activeSection === "custom" && disableCustomSubmit)}
          sx={{ backgroundColor: "#627CFB" }}
        >
          {isLoading ? "Starting…" : "Start Sync"}
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
};
